// Dashboard tile: counts of active-commission partners still missing
// setup details (tax profile, contact email, commission alias). Clicking a
// tile deep-links to /commissions with an `?admin=` param so the right
// bulk-editor pops open on arrival. Visible to staff / super admins /
// partner success — hidden from partner accounts.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ChevronRight, ShieldAlert, Mail, Link2, CheckCircle2, Rocket } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";

export function PartnerReadinessWidget() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get("/commissions/partners/readiness");
        setData(d);
      } catch { setData({ visible: false }); }
      finally { setLoading(false); }
    })();
  }, []);
  if (loading || !data?.visible) return null;

  const items = [
    { key: "tax",   label: "Missing tax fields",  count: data.missing_tax,   icon: ShieldAlert, admin: "missing-tax" },
    { key: "email", label: "Missing contact email", count: data.missing_email, icon: Mail,        admin: "missing-email" },
    { key: "alias", label: "Missing commission alias", count: data.missing_alias, icon: Link2,   admin: "alias-backfill" },
  ];
  const allGood = data.missing_tax === 0 && data.missing_email === 0 && data.missing_alias === 0;

  return (
    <Card data-testid="partner-readiness-widget" className="border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 px-5 py-3">
        <AlertTriangle className={`h-4 w-4 ${allGood ? "text-emerald-500" : "text-amber-500"}`} />
        <h3 className="font-heading text-sm font-semibold text-slate-800 dark:text-slate-100">Partner readiness</h3>
        <span className="ml-2 rounded bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[10px] font-medium text-slate-600 dark:text-slate-300"
              data-testid="partner-readiness-total">
          {data.total_active} active
        </span>
        {allGood && (
          <span className="ml-auto inline-flex items-center gap-1 rounded bg-emerald-100 dark:bg-emerald-500/15 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-300"
                data-testid="partner-readiness-all-good">
            <CheckCircle2 className="h-3 w-3" /> All caught up
          </span>
        )}
        {!allGood && (
          <button onClick={() => navigate("/commissions?admin=tour")}
                  data-testid="partner-readiness-tour"
                  className="ml-auto inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-[11px] font-medium text-primary-foreground hover:bg-primary/90 shadow-sm">
            <Rocket className="h-3 w-3" /> Start guided setup
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 gap-2 px-5 py-4 sm:grid-cols-3">
        {items.map((it) => {
          const Icon = it.icon;
          const done = it.count === 0;
          return (
            <button key={it.key}
                    onClick={() => navigate(`/commissions?admin=${it.admin}`)}
                    data-testid={`partner-readiness-${it.key}`}
                    className={`group flex items-center gap-3 rounded-md border p-3 text-left transition ${
                      done
                        ? "border-emerald-300 bg-emerald-50 hover:bg-emerald-100 dark:border-emerald-500/25 dark:bg-emerald-500/10 dark:hover:bg-emerald-500/15"
                        : "border-amber-300 bg-amber-100/80 hover:bg-amber-100 dark:border-amber-500/25 dark:bg-amber-500/10 dark:hover:bg-amber-500/15"
                    }`}>
              <Icon className={`h-5 w-5 shrink-0 ${done ? "text-emerald-600" : "text-amber-700 dark:text-amber-400"}`} />
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-wider text-slate-900 dark:text-slate-200 font-semibold truncate">{it.label}</div>
                <div className={`text-2xl font-bold tabular-nums ${done ? "text-emerald-700 dark:text-emerald-300" : "text-slate-900 dark:text-slate-100"}`}
                     data-testid={`partner-readiness-${it.key}-count`}>
                  {it.count}
                </div>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 text-slate-400 dark:text-slate-600 group-hover:text-slate-600 dark:group-hover:text-slate-400 transition" />
            </button>
          );
        })}
      </div>
    </Card>
  );
}
